import EditProfile from "../components/EditableProfile/EditProfile";
import {
  EorEmployeeProfileInput,
  useEditEorEmployeeProfileMutation,
  useGetEoRMemberByUuidQuery,
} from "types/generated/operations";
import { useProfileContextData } from "../contexts/ProfileInfoContext";
import { useParams } from "react-router-dom";
import { Alert, Box, Spinner } from "@justworkshr/milo-core";
import styles from "./EditProfilePage.module.css";
import EditableProfileHeader from "../components/EditableProfile/components/EditableProfileHeader";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useIsOwnProfile from "../hooks/useIsOwnProfile";
import useIsAdmin from "../hooks/useIsAdmin";
import { useProfileRedirectUrl } from "../hooks/useProfileRedirectUrl";
import { eorMemberSettingsReturnUrl } from "../neonReturnUrl";

const EditProfilePage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { loading: loadingMemberId, data: memberData } =
    useGetEoRMemberByUuidQuery({
      variables: { memberUuid: id },
    });
  const { profileInfo, loadingProfileInfo } = useProfileContextData(); //use this to set init values.
  const [updateEorProfile] = useEditEorEmployeeProfileMutation();
  const isOwnProfile = useIsOwnProfile();
  const { loading: loadingIsAdmin, isAdmin } = useIsAdmin();
  const profileRedirectUrl = useProfileRedirectUrl();

  const linkToProfile =
    isAdmin && !isOwnProfile
      ? profileRedirectUrl
      : eorMemberSettingsReturnUrl();

  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  async function onSubmit(input: EorEmployeeProfileInput) {
    if (!memberData) throw new Error("Unexpectedly found no member data");

    if (
      input.personalInfo?.esChildrensBirthDates &&
      input.personalInfo.esChildrensBirthDates.length === 0
    ) {
      input.personalInfo.esChildrensBirthDates = null;
    }

    await updateEorProfile({
      variables: {
        memberId: memberData.eorMember.id,
        profile: input,
      },
    });
  }

  let pageContent;

  if (loadingProfileInfo || loadingMemberId || loadingIsAdmin) {
    pageContent = (
      <Box padding="2xl">
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      </Box>
    );
  } else if (memberData && profileInfo) {
    pageContent = (
      <EditProfile
        profile={profileInfo.eorEmployeeProfile}
        memberData={memberData.eorMember}
        onSubmit={onSubmit}
        onError={setErrorMessage}
        clearError={() => setErrorMessage(undefined)}
        linkToProfile={linkToProfile}
      />
    );
  } else {
    // This should never happen
    pageContent = <div>Unknown Error</div>;
    console.error("The EoR member and/or profile data could not be retrieved.");
  }

  return (
    <>
      {errorMessage && <Alert color="destructive">{errorMessage}</Alert>}
      <div className={styles.pageContainer}>
        <EditableProfileHeader
          linkToProfile={linkToProfile}
          title={t("Edit Profile")}
        />
        {pageContent}
      </div>
    </>
  );
};

export default EditProfilePage;
